import { useModal } from '@solinkcorp/solink-components';
import { Button, type ColumnDef, DataTable } from '@solinkcorp/solink-ui';
import { setFilter } from 'actions/filterActions';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import {
  getExpirationText,
  getFrequencyText,
  getLocationCount,
  type RepeatOptionValue,
} from 'components/SpotCheckPanel/Aspen/spotCheckTableFormatters';
import { useEventSpotCheckMonthlyUsages } from 'components/SpotCheckPanel/hooks/useEventSpotCheckMonthlyUsage';
import { useMultiScheduledSpotCheckMonthlyUsages } from 'components/SpotCheckPanel/hooks/useScheduledSpotCheckMonthlyUsage';
import SpotCheckDeleteConfirmationModal from 'components/SpotCheckPanel/SpotCheckMainContent/SpotCheckTemplatesView/SpotCheckTable/Aspen/SpotCheckDeleteConfirmationModal';
import { Copy, Eye, Loader2, Pencil, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import { useDispatch, useSelector } from 'stores/store';
import { getRepeatOptionValue } from 'utils/RecurrenceUtil';
import { type SpotCheckView } from '../SpotCheckMainContent/SpotCheckMainContent';
import { type SpotCheckCategoryKey } from '../SpotCheckMainContent/SpotCheckTemplatesView/SpotCheckTemplatesView';

const PAGE_SIZE = 25;

const TABLE_CLASS =
  'tw w-full min-w-0 border-border-muted bg-background-surface [&_[data-slot=table-cell]]:border-border-muted [&_[data-slot=table-cell]]:text-foreground-primary [&_[data-slot=table-head]]:border-border-muted [&_[data-slot=table-head]]:text-foreground-primary';

type ScheduledSpotCheckRow = {
  spotCheck: ScheduledActivityEntity;
  title: string;
  locations: number;
  frequency: string;
  expiration: string;
  monthlyUsage?: string | number;
};

type EventSpotCheckRow = {
  eventSpotCheck: AiNotification;
  title: string;
  estimatedMonthlyUsage: number | null;
};

interface SpotCheckTableProps {
  setView: React.Dispatch<React.SetStateAction<SpotCheckView>>;
  spotChecks: ScheduledActivityEntity[];
  eventSpotChecks: AiNotification[];
  selectedSpotCheckCategoryKey: SpotCheckCategoryKey | null;
  setSpotCheck: React.Dispatch<React.SetStateAction<ScheduledActivityEntity | undefined>>;
  setEventSpotCheck: React.Dispatch<React.SetStateAction<AiNotification | undefined>>;
  spotCheck: ScheduledActivityEntity | undefined;
  eventSpotCheck: AiNotification | undefined;
  setFormMode: React.Dispatch<React.SetStateAction<'create' | 'edit'>>;
  setIsExistingSpotCheck: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SpotCheckTable(props: SpotCheckTableProps) {
  const {
    setView,
    spotChecks,
    eventSpotChecks,
    selectedSpotCheckCategoryKey,
    setSpotCheck,
    setEventSpotCheck,
    spotCheck,
    eventSpotCheck,
    setFormMode,
    setIsExistingSpotCheck,
  } = props;

  const dispatch = useDispatch();
  const locale = useLocale();
  const [pageIndex, setPageIndex] = useState(0);
  const camerasById = useFluxStore(LocationStore, (store) => store.getAllLocationCamerasDict());
  const spotCheckDeleteModalProps = useModal();

  const { spotCheckMode } = useSelector((state) => ({
    spotCheckMode: state.filter.spotCheckMode,
  }));

  const isActiveMode = selectedSpotCheckCategoryKey === 'active';
  const isEventSpotCheck = spotCheckMode === 'event';

  const handleEditSpotCheck = useCallback(
    (rowSpotCheck: ScheduledActivityEntity) => {
      setFormMode(isActiveMode ? 'edit' : 'create');
      setIsExistingSpotCheck(true);
      setView('spot-check-form');
      setSpotCheck(rowSpotCheck);
      dispatch(
        setFilter({ selectedCameraIds: rowSpotCheck.cameraIds, spotCheckMode: 'scheduled' }),
      );
    },
    [dispatch, isActiveMode, setFormMode, setSpotCheck, setView, setIsExistingSpotCheck],
  );

  const handleCopySpotCheck = useCallback(
    (spotCheckCopy: ScheduledActivityEntity) => {
      setFormMode('create');
      setIsExistingSpotCheck(false);
      setView('spot-check-form');
      setSpotCheck(spotCheckCopy);
      dispatch(
        setFilter({ selectedCameraIds: spotCheckCopy.cameraIds, spotCheckMode: 'scheduled' }),
      );
    },
    [dispatch, setFormMode, setIsExistingSpotCheck, setView, setSpotCheck],
  );

  const handleCopyEventSpotCheck = useCallback(
    (eventSpotCheckCopy: AiNotification) => {
      setFormMode('create');
      setIsExistingSpotCheck(false);
      setView('spot-check-form');
      setEventSpotCheck(eventSpotCheckCopy);
      dispatch(setFilter({ spotCheckMode: 'event' }));
    },
    [dispatch, setFormMode, setIsExistingSpotCheck, setView, setEventSpotCheck],
  );

  const handleEditEventSpotCheck = useCallback(
    (rowEventSpotCheck: AiNotification) => {
      setFormMode(isActiveMode ? 'edit' : 'create');
      setIsExistingSpotCheck(true);
      setView('spot-check-form');
      setEventSpotCheck(rowEventSpotCheck);
      dispatch(setFilter({ spotCheckMode: 'event' }));
    },
    [dispatch, isActiveMode, setFormMode, setEventSpotCheck, setView, setIsExistingSpotCheck],
  );

  const handleDeleteSpotCheck = useCallback(
    (rowSpotCheck: ScheduledActivityEntity) => {
      setEventSpotCheck(undefined);
      setSpotCheck(rowSpotCheck);
      spotCheckDeleteModalProps.openModal();
    },
    [setEventSpotCheck, setSpotCheck, spotCheckDeleteModalProps],
  );

  const handleDeleteEventSpotCheck = useCallback(
    (rowEventSpotCheck: AiNotification) => {
      setSpotCheck(undefined);
      setEventSpotCheck(rowEventSpotCheck);
      spotCheckDeleteModalProps.openModal();
    },
    [setSpotCheck, setEventSpotCheck, spotCheckDeleteModalProps],
  );

  const spotChecksForMonthlyUsage = useMemo(
    () => (isActiveMode ? spotChecks : []),
    [isActiveMode, spotChecks],
  );

  const { monthlyUsages: scheduledSpotCheckMonthlyUsages } =
    useMultiScheduledSpotCheckMonthlyUsages(spotChecksForMonthlyUsage);
  const { monthlyUsages: eventSpotCheckMonthlyUsages, loading: eventSpotCheckLoading } =
    useEventSpotCheckMonthlyUsages(eventSpotChecks);

  const scheduledData = useMemo(
    (): ScheduledSpotCheckRow[] =>
      spotChecks.map((rowSpotCheck) => ({
        spotCheck: rowSpotCheck,
        title: rowSpotCheck.title,
        locations: getLocationCount(rowSpotCheck, camerasById),
        frequency: getFrequencyText(
          getRepeatOptionValue(
            rowSpotCheck.rule,
            rowSpotCheck.reoccurringCount,
          ) as RepeatOptionValue,
          locale,
        ),
        expiration: getExpirationText(rowSpotCheck.endsAt, rowSpotCheck.reoccurringCount, locale),
        monthlyUsage: isActiveMode
          ? (scheduledSpotCheckMonthlyUsages[rowSpotCheck.id] ?? locale.scheduledAIChat.calculating)
          : undefined,
      })),
    [spotChecks, scheduledSpotCheckMonthlyUsages, isActiveMode, camerasById, locale],
  );

  const eventData = useMemo(
    (): EventSpotCheckRow[] =>
      eventSpotChecks.map((rowEventSpotCheck) => ({
        eventSpotCheck: rowEventSpotCheck,
        title: rowEventSpotCheck?.metadata?.title ?? locale.scheduledAIChat.eventSpotCheck,
        estimatedMonthlyUsage: eventSpotCheckMonthlyUsages?.[rowEventSpotCheck.id ?? ''] ?? null,
      })),
    [eventSpotChecks, eventSpotCheckMonthlyUsages, locale],
  );

  const listKey = `${isEventSpotCheck ? 'event' : 'scheduled'}:${selectedSpotCheckCategoryKey ?? ''}`;
  const rowCount = isEventSpotCheck ? eventData.length : scheduledData.length;
  const previousListKey = useRef(listKey);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(rowCount / PAGE_SIZE) - 1);
    if (previousListKey.current !== listKey) {
      previousListKey.current = listKey;
      setPageIndex(0);
      return;
    }
    setPageIndex((current) => Math.min(current, lastPage));
  }, [listKey, rowCount]);

  const scheduledColumns = useMemo((): ColumnDef<ScheduledSpotCheckRow>[] => {
    const columns: ColumnDef<ScheduledSpotCheckRow>[] = [
      {
        accessorKey: 'title',
        header: locale.fields.title,
      },
      {
        accessorKey: 'locations',
        header: locale.general.locations,
      },
      {
        accessorKey: 'frequency',
        header: locale.scheduledAIChat.frequency,
      },
    ];

    if (isActiveMode) {
      columns.push({
        accessorKey: 'monthlyUsage',
        header: locale.scheduledAIChat.monthlyUsage,
      });
    }

    columns.push(
      {
        accessorKey: 'expiration',
        header: locale.scheduledAIChat.expiration,
      },
      {
        id: 'actions',
        enableSorting: false,
        header: '',
        cell: ({ row }) => (
          <div className="tw flex justify-end gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={isActiveMode ? locale.general.edit : locale.general.view}
              title={isActiveMode ? locale.general.edit : locale.general.view}
              onClick={(event) => {
                event.stopPropagation();
                handleEditSpotCheck(row.original.spotCheck);
              }}
            >
              {isActiveMode ? <Pencil className="size-4" /> : <Eye className="size-4" />}
            </Button>
            {isActiveMode ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={locale.general.copy}
                title={locale.general.copy}
                onClick={(event) => {
                  event.stopPropagation();
                  const TEN_MINUTES_MS = 10 * 60 * 1000;
                  const duplicatedSpotCheck = {
                    ...row.original.spotCheck,
                    title: row.original.spotCheck.title + ' - COPY',
                    startsAt: Date.now() + TEN_MINUTES_MS,
                  };
                  handleCopySpotCheck(duplicatedSpotCheck);
                }}
              >
                <Copy className="size-4" />
              </Button>
            ) : null}
            {isActiveMode ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={locale.general.delete}
                title={locale.general.delete}
                className="text-destructive hover:text-destructive"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteSpotCheck(row.original.spotCheck);
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            ) : null}
          </div>
        ),
      },
    );

    return columns;
  }, [isActiveMode, locale, handleDeleteSpotCheck, handleEditSpotCheck, handleCopySpotCheck]);

  const eventColumns = useMemo((): ColumnDef<EventSpotCheckRow>[] => {
    return [
      {
        accessorKey: 'title',
        header: locale.fields.title,
      },
      {
        accessorKey: 'estimatedMonthlyUsage',
        header: locale.scheduledAIChat.monthlyUsage,
        cell: ({ row }) => {
          const value = row.original.estimatedMonthlyUsage;
          if (value) return value;
          if (eventSpotCheckLoading) {
            return <Loader2 className="size-4 animate-spin text-foreground-secondary" />;
          }
          return null;
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        header: '',
        cell: ({ row }) => (
          <div className="tw flex justify-end gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={isActiveMode ? locale.general.edit : locale.general.view}
              title={isActiveMode ? locale.general.edit : locale.general.view}
              onClick={(event) => {
                event.stopPropagation();
                handleEditEventSpotCheck(row.original.eventSpotCheck);
              }}
            >
              {isActiveMode ? <Pencil className="size-4" /> : <Eye className="size-4" />}
            </Button>
            {isActiveMode ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={locale.general.copy}
                title={locale.general.copy}
                onClick={(event) => {
                  event.stopPropagation();
                  const original = row.original.eventSpotCheck;
                  const duplicatedEventSpotCheck: AiNotification = {
                    ...original,
                    reportTemplateId: '',
                    metadata: {
                      ...original.metadata,
                      title: (original.metadata?.title ?? '') + ' - COPY',
                    },
                  };
                  handleCopyEventSpotCheck(duplicatedEventSpotCheck);
                }}
              >
                <Copy className="size-4" />
              </Button>
            ) : null}
            {isActiveMode ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={locale.general.delete}
                title={locale.general.delete}
                className="text-destructive hover:text-destructive"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteEventSpotCheck(row.original.eventSpotCheck);
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [
    isActiveMode,
    locale,
    eventSpotCheckLoading,
    handleDeleteEventSpotCheck,
    handleEditEventSpotCheck,
    handleCopyEventSpotCheck,
  ]);

  const paginationProps = {
    pageSize: PAGE_SIZE,
    pagination: { pageIndex, pageSize: PAGE_SIZE } as const,
    onPaginationChange: (
      updater: Parameters<
        NonNullable<React.ComponentProps<typeof DataTable>['onPaginationChange']>
      >[0],
    ) => {
      setPageIndex((current) => {
        const next =
          typeof updater === 'function'
            ? updater({ pageIndex: current, pageSize: PAGE_SIZE })
            : updater;
        return next.pageIndex;
      });
    },
    autoResetPageIndex: false as const,
    enableExtend: true as const,
  };

  return (
    <div className="tw w-full min-w-0">
      {isEventSpotCheck ? (
        <DataTable
          className={TABLE_CLASS}
          columns={eventColumns}
          data={eventData}
          {...paginationProps}
          getRowId={(row) => row.eventSpotCheck.id ?? ''}
        />
      ) : (
        <DataTable
          className={TABLE_CLASS}
          columns={scheduledColumns}
          data={scheduledData}
          {...paginationProps}
          getRowId={(row) => row.spotCheck.id}
        />
      )}
      <SpotCheckDeleteConfirmationModal
        {...spotCheckDeleteModalProps}
        spotCheck={spotCheck}
        eventSpotCheck={eventSpotCheck}
      />
    </div>
  );
}
